import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import { jobsApi, companiesApi } from '../lib/api';
import { useDataRefresh, DATA_EVENTS } from '../contexts/DataRefreshContext';
import { usePageVisitLogger, logSearch, logFilter } from '../hooks/useActivityLogger';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { StatusBadge, PriorityBadge } from '../components/common/Badges';
import FileUpload from '../components/common/FileUpload';
import Pagination from '../components/common/Pagination';
import {
  Plus,
  Search,
  Upload,
  MapPin,
  Clock,
  Briefcase,
  Building2,
  Loader2,
  Eye,
  Trash2,
  CheckSquare,
  X,
  DollarSign,
  Percent,
  Mail,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

export default function JobsPage() {
  // Activity logging
  usePageVisitLogger('Jobs');
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { triggerRefresh } = useDataRefresh();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [sourceFilter, setSourceFilter] = useState(() => searchParams.get('source') || '');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const itemsPerPage = 12;

  // Sync state to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    if (sourceFilter) params.set('source', sourceFilter);
    setSearchParams(params, { replace: true });
  }, [page, search, statusFilter, sourceFilter, setSearchParams]);

  // Bulk action states
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [bulkBillingMode, setBulkBillingMode] = useState('single'); // 'single' or 'individual'
  const [singleBillingRate, setSingleBillingRate] = useState({ percentage: '', cost: '' });
  const [individualBilling, setIndividualBilling] = useState({});
  const [savingBulk, setSavingBulk] = useState(false);
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailBody, setBulkEmailBody] = useState('');

  useEffect(() => {
    loadJobs();
  }, [page, search, statusFilter, sourceFilter]);

  useEffect(() => {
    setSelectedIds([]);
  }, [page]);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const response = await jobsApi.list({
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
      });
      setJobs(response.data.jobs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Company search function for type-ahead
  const searchCompanies = async (query) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies?search=${encodeURIComponent(query)}&limit=20`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
      });
      const data = await response.json();
      return (data.companies || []).map(c => ({ ...c, name: c.company_name || c.name }));
    } catch (error) {
      console.error('Failed to search companies:', error);
      return [];
    }
  };

  const selectAll = () => {
    if (selectedIds.length === jobs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(jobs.map(j => j.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    if (!window.confirm(`Delete ${selectedIds.length} job(s)? This cannot be undone.`)) return;
    
    try {
      const response = await jobsApi.bulkDelete(selectedIds);
      toast.success(response.data.message);
      setSelectedIds([]);
      setSelectMode(false);
      loadJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete jobs');
    }
  };

  const cancelSelection = () => {
    setSelectedIds([]);
    setSelectMode(false);
  };

  const handleUpload = async (file, aiProvider) => {
    try {
      const response = await jobsApi.uploadJD(file, aiProvider);
      toast.success(`Job "${response.data.job_title}" created successfully`);
      setUploadOpen(false);
      triggerRefresh(DATA_EVENTS.JOB_CREATED, { jobId: response.data.id });
      loadJobs();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload JD');
      throw error;
    }
  };

  const handleDelete = async (jobId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      await jobsApi.delete(jobId);
      toast.success('Job deleted');
      triggerRefresh(DATA_EVENTS.JOB_DELETED, { jobId });
      loadJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    }
  };

  const handleBulkEmail = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one job');
      return;
    }
    
    setSendingBulkEmail(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/apollo/bulk-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ats_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_ids: selectedIds,
          custom_subject: bulkEmailSubject || null,
          custom_body: bulkEmailBody || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.message}`);
        setBulkEmailModalOpen(false);
        setBulkEmailSubject('');
        setBulkEmailBody('');
        setSelectedIds([]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send bulk emails');
      }
    } catch (error) {
      toast.error('Failed to send bulk emails');
    } finally {
      setSendingBulkEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const loadCompanies = async () => {
    try {
      const response = await companiesApi.list({ limit: 200 });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Failed to load companies');
    }
  };

  const openBillingModal = () => {
    // Initialize individual billing with current values
    const initial = {};
    selectedIds.forEach(id => {
      const job = jobs.find(j => j.id === id);
      if (job) {
        initial[id] = {
          title: job.job_title,
          percentage: job.billing_rate_percentage || '',
          cost: job.billing_rate_cost || ''
        };
      }
    });
    setIndividualBilling(initial);
    setSingleBillingRate({ percentage: '', cost: '' });
    setBulkBillingMode('single');
    setBillingModalOpen(true);
  };

  const handleBulkBillingUpdate = async () => {
    if (selectedIds.length === 0) return;
    setSavingBulk(true);

    try {
      if (bulkBillingMode === 'single') {
        const billingData = {};
        if (singleBillingRate.percentage) {
          billingData.billing_rate_percentage = parseFloat(singleBillingRate.percentage);
        }
        if (singleBillingRate.cost) {
          billingData.billing_rate_cost = parseFloat(singleBillingRate.cost);
        }
        
        if (Object.keys(billingData).length === 0) {
          toast.error('Please enter at least one billing value');
          return;
        }

        const response = await jobsApi.bulkUpdateBilling(selectedIds, billingData);
        toast.success(response.data.message);
      } else {
        // Individual mode
        const updates = Object.entries(individualBilling).map(([jobId, data]) => ({
          job_id: jobId,
          billing_rate_percentage: data.percentage ? parseFloat(data.percentage) : null,
          billing_rate_cost: data.cost ? parseFloat(data.cost) : null
        })).filter(u => u.billing_rate_percentage || u.billing_rate_cost);

        if (updates.length === 0) {
          toast.error('Please enter at least one billing value');
          return;
        }

        const response = await jobsApi.bulkUpdateBillingIndividual(updates);
        toast.success(response.data.message);
      }

      setBillingModalOpen(false);
      loadJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update billing');
    } finally {
      setSavingBulk(false);
    }
  };

  const handleBulkCompanyAssign = async () => {
    if (selectedIds.length === 0 || !selectedCompanyId) return;
    setSavingBulk(true);

    try {
      const response = await jobsApi.bulkAssignCompany(selectedIds, selectedCompanyId);
      toast.success(response.data.message);
      setCompanyModalOpen(false);
      setSelectedCompanyId('');
      loadJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign company');
    } finally {
      setSavingBulk(false);
    }
  };

  const selectedJobsData = jobs.filter(j => selectedIds.includes(j.id));

  return (
    <div>
      <Header title="Jobs" subtitle={`${total} total positions`}>
        {selectMode ? (
          <>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>{selectedIds.length} selected</span>
            </div>
            <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-jobs-btn">
              {selectedIds.length === jobs.length ? 'Deselect All' : 'Select All'}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={openBillingModal}
              disabled={selectedIds.length === 0}
              data-testid="bulk-billing-btn"
            >
              <Percent className="w-4 h-4 mr-2" />
              Billing Rate
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCompanyModalOpen(true)}
              disabled={selectedIds.length === 0}
              data-testid="bulk-company-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Assign Company
            </Button>
            {sourceFilter === 'apollo_import' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setBulkEmailModalOpen(true)}
                disabled={selectedIds.length === 0}
                className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                data-testid="bulk-email-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Bulk Email
              </Button>
            )}
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={handleBulkDelete}
              disabled={selectedIds.length === 0}
              data-testid="bulk-delete-jobs-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
            <Button variant="ghost" size="sm" onClick={cancelSelection}>
              <X className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => setSelectMode(true)} data-testid="select-jobs-mode-btn">
              <CheckSquare className="w-4 h-4 mr-2" />
              Select
            </Button>
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button data-testid="upload-jd-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload JD
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Upload Job Description</DialogTitle>
                </DialogHeader>
                <FileUpload
                  onUpload={handleUpload}
                  title="Upload Job Description"
                  description="AI will parse and extract job requirements"
                />
              </DialogContent>
            </Dialog>
            
            <Button variant="outline" onClick={() => navigate('/ats/jobs/new')} data-testid="create-job-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Manual
            </Button>
          </>
        )}
      </Header>

      <div className="page-container">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search jobs..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
              data-testid="search-jobs"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-40 max-md:w-full max-md:min-h-[44px]" data-testid="filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="filled">Filled</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={sourceFilter || 'all'} onValueChange={(v) => { setSourceFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-44 max-md:w-full max-md:min-h-[44px]" data-testid="filter-source">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="apollo_import">Apollo Import</SelectItem>
              <SelectItem value="manual">Manual Entry</SelectItem>
              <SelectItem value="jd_upload">JD Upload</SelectItem>
              <SelectItem value="webhook">Webhook</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Jobs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : jobs.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-4">No jobs found</p>
              <Button onClick={() => setUploadOpen(true)} data-testid="empty-upload-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload First JD
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={`/ats/jobs/${job.id}`}
                className="block"
                data-testid={`job-card-${job.id}`}
              >
                <Card className="h-full card-hover">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {job.job_title}
                        </h3>
                        {job.company_name && (
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                            <Building2 className="w-3.5 h-3.5" />
                            {job.company_name}
                          </p>
                        )}
                      </div>
                      <StatusBadge status={job.status} />
                    </div>

                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      {job.primary_location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="truncate">{job.primary_location}</span>
                        </div>
                      )}
                      {(job.min_experience_years !== null || job.max_experience_years !== null) && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span>
                            {job.min_experience_years || 0}
                            {job.max_experience_years ? `-${job.max_experience_years}` : '+'} years
                          </span>
                        </div>
                      )}
                      {job.job_type && (
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-slate-400" />
                          <span>{job.job_type}</span>
                        </div>
                      )}
                    </div>

                    {/* Skills */}
                    {job.must_have_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {job.must_have_skills.slice(0, 4).map((skill, i) => (
                          <span key={i} className="skill-tag">{skill}</span>
                        ))}
                        {job.must_have_skills.length > 4 && (
                          <span className="skill-tag">+{job.must_have_skills.length - 4}</span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100 max-md:flex-wrap max-md:gap-y-2">
                      <div className="flex items-center gap-2">
                        <PriorityBadge priority={job.priority_level} />
                        <span className="text-xs text-slate-400">
                          {job.number_of_openings} opening{job.number_of_openings !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 max-md:gap-2 max-md:[&_button]:inline-flex max-md:[&_button]:items-center max-md:[&_button]:justify-center max-md:[&_button]:min-h-[44px] max-md:[&_button]:min-w-[44px]">
                        <button
                          onClick={(e) => { 
                            e.preventDefault(); 
                            e.stopPropagation();
                            navigate(`/ats/jobs/${job.id}/candidates`); 
                          }}
                          className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                          title="Find Matching Candidates"
                          data-testid={`find-candidates-job-${job.id}`}
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); navigate(`/ats/jobs/${job.id}`); }}
                          className="p-1.5 text-slate-400 hover:text-slate-600 rounded"
                          data-testid={`view-job-${job.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDelete(job.id, e)}
                          className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                          data-testid={`delete-job-${job.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > itemsPerPage && (
          <div className="mt-6">
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(total / itemsPerPage)}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Bulk Billing Rate Modal */}
      <Dialog open={billingModalOpen} onOpenChange={setBillingModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Update Billing Rates ({selectedIds.length} jobs)
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={bulkBillingMode} onValueChange={setBulkBillingMode} className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="single" className="flex-1">Same Value for All</TabsTrigger>
              <TabsTrigger value="individual" className="flex-1">Individual Values</TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="mt-4 space-y-4">
              <p className="text-sm text-slate-600">
                Set the same billing rate for all {selectedIds.length} selected jobs.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Billing Rate (% of CTC)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="e.g., 8.33"
                    value={singleBillingRate.percentage}
                    onChange={(e) => setSingleBillingRate(prev => ({ ...prev, percentage: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">8.33% = 1 month CTC</p>
                </div>
                <div>
                  <Label>OR Fixed Cost (₹)</Label>
                  <Input
                    type="number"
                    step="1000"
                    min="0"
                    placeholder="e.g., 50000"
                    value={singleBillingRate.cost}
                    onChange={(e) => setSingleBillingRate(prev => ({ ...prev, cost: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">Fixed fee per placement</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="individual" className="mt-4 space-y-4">
              <p className="text-sm text-slate-600">
                Set individual billing rates for each job.
              </p>
              <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                {selectedJobsData.map(job => (
                  <div key={job.id} className="p-3 border border-slate-200 rounded-lg">
                    <p className="font-medium text-slate-800 mb-2">{job.job_title}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">% of CTC</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder="8.33"
                          value={individualBilling[job.id]?.percentage || ''}
                          onChange={(e) => setIndividualBilling(prev => ({
                            ...prev,
                            [job.id]: { ...prev[job.id], title: job.job_title, percentage: e.target.value }
                          }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Fixed Cost (₹)</Label>
                        <Input
                          type="number"
                          step="1000"
                          min="0"
                          placeholder="50000"
                          value={individualBilling[job.id]?.cost || ''}
                          onChange={(e) => setIndividualBilling(prev => ({
                            ...prev,
                            [job.id]: { ...prev[job.id], title: job.job_title, cost: e.target.value }
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setBillingModalOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkBillingUpdate} disabled={savingBulk}>
              {savingBulk ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Update Billing Rates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Company Assignment Modal */}
      <Dialog open={companyModalOpen} onOpenChange={setCompanyModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Assign Company ({selectedIds.length} jobs)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Select a company to assign to the {selectedIds.length} selected jobs.
            </p>
            
            <div>
              <Label>Select Company <span className="text-xs text-slate-500">(Type 3+ chars)</span></Label>
              <SearchableSelect
                value={selectedCompanyId}
                onValueChange={setSelectedCompanyId}
                onSearch={searchCompanies}
                options={companies.map(c => ({ ...c, name: c.company_name || c.name }))}
                placeholder="Type company name..."
                minChars={3}
                displayKey="name"
                valueKey="id"
                renderOption={(company) => (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span>{company.name || company.company_name}</span>
                  </div>
                )}
              />
            </div>

            {selectedIds.length > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Selected Jobs:</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {selectedJobsData.map(job => (
                    <p key={job.id} className="text-sm text-slate-600">• {job.job_title}</p>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanyModalOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkCompanyAssign} disabled={savingBulk || !selectedCompanyId}>
              {savingBulk ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Assign Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Email Modal */}
      <Dialog open={bulkEmailModalOpen} onOpenChange={setBulkEmailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-600" />
              Send Bulk Email to HR Contacts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                <strong>{selectedIds.length} jobs selected</strong> - Emails will be sent to HR contacts from Apollo-imported jobs.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Custom Subject (optional)</Label>
              <Input
                value={bulkEmailSubject}
                onChange={(e) => setBulkEmailSubject(e.target.value)}
                placeholder="Leave empty for default subject"
              />
              <p className="text-xs text-slate-500">Default: "Job Opportunity at [Company Name]"</p>
            </div>
            
            <div className="space-y-2">
              <Label>Custom Message (optional)</Label>
              <textarea
                value={bulkEmailBody}
                onChange={(e) => setBulkEmailBody(e.target.value)}
                placeholder="Leave empty for default template. Use {{name}}, {{company_name}}, {{job_title}} for variables."
                className="w-full h-32 p-3 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500">Variables: {"{{name}}"}, {"{{company_name}}"}, {"{{job_title}}"}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEmailModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkEmail} 
              disabled={sendingBulkEmail}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingBulkEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Send Emails
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
